/**
 * Generation codes for select models—focusing on BMW.
 *
 * - The BMW 3 Series (e.g. a 330i) is of the G20 generation.
 *   Even if someone fits it with a "G80 conversion kit," it remains a G20.
 *
 * - The BMW M3 is a genuine high-performance model built on the G80 platform.
 */
export const vehicleGenerationCodes = {
  BMW: {
    "3 Series": {
      generation: "G20",
      isG80: false,
      note: "The BMW 3 Series (e.g., 330i) is part of the G20 generation. A conversion kit does not change its inherent G20 status.",
    },
    M3: {
      generation: "G80",
      isG80: true,
      note: "The BMW M3 is a genuine high-performance model built on the G80 platform.",
    },
  },
  // (Additional generation codes for other makes can be added as needed.)
};

/**
 * Utility function to determine if a given BMW is a genuine G80 model.
 *
 * @param {string} make - The vehicle make (e.g., "BMW").
 * @param {string} model - The vehicle model (e.g., "3 Series" or "M3").
 * @param {boolean} hasG80ConversionKit - Whether the car has been fitted with a G80 conversion kit.
 * @returns {boolean} - Returns true only if the car is genuinely a G80 model.
 *
 * Examples:
 * - A BMW M3 (even without a conversion kit) is genuine G80.
 * - A BMW 3 Series (e.g., 330i) remains G20 even if someone applies a G80 conversion kit.
 */
export const isG80Car = (make, model, hasG80ConversionKit = false) => {
  if (make === "BMW") {
    if (model === "M3") return true; // Genuine BMW M3 is G80.
    if (model === "3 Series") return false; // BMW 3 Series is G20, regardless of conversion.
  }
  return false;
};

/**
 * A comprehensive list of common vehicle colors, including manufacturer-specific colors.
 */
export const vehicleColors = [
  // Basic Colors
  "Black",
  "White",
  "Gray",
  "Silver",
  "Red",
  "Blue",
  "Cyan",
  "Green",
  "Yellow",
  "Orange",
  "Brown",
  "Maroon",
  "Purple",
  "Gold",
  "Beige",
  "Pink",

  // Manufacturer Specific Colors
  "Mazda Soul Red Crystal",
  "Mazda Machine Gray",
  "Mazda Polymetal Gray",
  "BMW Frozen Gray",
  "BMW San Marino Blue",
  "BMW Isle of Man Green",
  "Mercedes Designo Diamond White",
  "Mercedes Selenite Grey",
  "Porsche Guards Red",
  "Porsche Miami Blue",
  "Porsche Chalk",
  "Audi Nardo Gray",
  "Subaru World Rally Blue",
  "Honda Sonic Pearl Gray",
  "Toyota Precious Galena",
  "Lexus Structural Blue",
  "Volvo Thunder Gray",
  "Volkswagen Lapiz Blue",

  // Premium/Special Finishes
  "Matte Black",
  "Matte White",
  "Satin Black",
  "Satin Silver",
  "Pearl White",
  "Carbon Black",
  "Frozen White",
  "Frozen Blue",
  "Chrome",
  "Brushed Aluminum",
  "Copper",
  "Bronze",
  "Rose Gold",
  "Chameleon",
  "Color Shift Purple-Blue",
  "Midnight Purple",

  // Classic/Historic Colors
  "British Racing Green",
  "Ferrari Rosso Corsa",
  "Lamborghini Verde Mantis",
  "Gulf Racing Blue",
  "Dodge Plum Crazy Purple",
  "Mercedes Tobacco Brown",

  // Standard Colors (Extended)
  "Champagne",
  "Burgundy",
  "Wine Red",
  "Gunmetal",
  "Graphite",
  "Charcoal",
  "Diamond White",
  "Cosmic Black",
  "Sapphire Blue",
  "Racing Green",
  "Metallic Gray",
  "Metallic Blue",
  "Metallic Green",
  "Metallic Bronze",
  "Arctic White",
  "Mystic Black",
  "Electric Blue",
  "Sunset Orange",
  "Desert Sand",
  "Forest Green",
  "Ocean Blue",
  "Titanium",
  "Platinum",

  // Enthusiast Colors
  "Initial D Panda",
  "Brian O'Conner F4 Orange Livery",
];

/**
 * A comprehensive (and slightly humorous) list of vehicle body styles.
 */
export const vehicleBodyStyles = [
  // Common Body Styles
  "Sedan",
  "Coupe",
  "Convertible",
  "Hatchback",
  "SUV",
  "Crossover",
  "Kei Car",
  "Truck",
  "Pickup",
  "Minivan",
  "Van",
  "Wagon",
  "Estate",
  "Shooting Brake",
  "Gran Coupe",
  "Gran Turismo",
  "Fastback",
  "Liftback",
  "Sport Activity Vehicle",
  "Sport Activity Coupe",
  "MPV",

  // Commercial Vehicles
  "Light Commercial Vehicle",
  "Panel Van",
  "Box Truck",
  "Flatbed",
  "Car Carrier",

  // Special/Performance
  "Supercar",
  "Hypercar",
  "Hot Hatch",
  "Sports Car",
  "Grand Tourer",
  "Roadster",

  // Specialty/Unique
  "Limousine",
  "Hearse",
  "Ambulance",
  "Police Interceptor",
  "Fire Truck",

  // Military/Special Purpose (Fun Additions)
  "Tank",
  "APC",
  "Attack Helicopter",
  "Monster Truck",
  "Batmobile",
  "Time Machine",
  "Flying Car",
  "Submarine Car",
  "Space Shuttle",
  "Lunar Rover",
  "Millennium Falcon",
  "TIE Fighter",
  "X-Wing",
  "Gundam",
  "Transformer",
  "DeLorean DMC-12",
  "Knight Rider KITT",
  "Mystery Machine",
  "Ghostbusters Ecto-1",
  "James Bond DB5",

  // Electric Vehicle Specific
  "Electric SUV",
  "Electric Crossover",
  "Electric Sedan",
  "Electric Sports Car",
  "Electric Hypercar",
];
